/**
 * Drawer derecho del Admin (design-system §7 · Drawer): panel de 480 px sobre Radix Dialog, para
 * ver el detalle de una fila sin perder la tabla. Entrada 240 ms `ease-emphasized`.
 */
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { t } from '../../i18n';
import { cn } from '../../lib/cn';

import type { ReactNode } from 'react';

export const Drawer = DialogPrimitive.Root;
export const DrawerTrigger = DialogPrimitive.Trigger;
export const DrawerClose = DialogPrimitive.Close;

export interface DrawerContentProps {
  title: string;
  description?: string;
  children: ReactNode;
  /** Acciones de la entidad: viven en la cabecera del drawer. */
  actions?: ReactNode;
  footer?: ReactNode;
  className?: string;
}

export function DrawerContent({
  title,
  description,
  children,
  actions,
  footer,
  className,
}: DrawerContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-fm-overlay" />
      <DialogPrimitive.Content
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex w-full max-w-[480px] flex-col',
          'border-l border-fm-border bg-fm-surface shadow-fm-2',
          'transition-transform duration-fm-slow ease-fm-emphasized motion-reduce:transition-none',
          className,
        )}
      >
        <header className="flex items-start justify-between gap-fm-3 border-b border-fm-border p-fm-5">
          <div className="min-w-0">
            <DialogPrimitive.Title className="font-display text-fm-xl font-bold text-fm-text">
              {title}
            </DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description className="mt-fm-1 text-fm-sm text-fm-text-secondary">
                {description}
              </DialogPrimitive.Description>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-fm-2">
            {actions}
            <DialogPrimitive.Close
              aria-label={t('common.buttons.close')}
              className="inline-flex size-fm-touch items-center justify-center rounded-fm-md text-fm-text-secondary hover:bg-fm-bg-sunken focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus"
            >
              <X size={20} aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-fm-5 text-fm-sm text-fm-text">{children}</div>

        {footer ? (
          <footer className="flex justify-end gap-fm-2 border-t border-fm-border p-fm-5">
            {footer}
          </footer>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
